/*
 * File: d:\projects\AutoFiFront\src\Services\Users\UserService.ts
 * Project: d:\projects\AutoFiFront
 * Created Date: Friday January 13th 2023
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Friday January 13th 2023 2:10:45 pm
 * Modified By: the developer formerly known as Jonathan Mazat at <you@you.you>
 * -----
 * Copyright (c) 2023 Autobuy SAS
 * -----
 * HISTORY:
 */

import Axios from "axios";
import Config from "Config/Config";
import ServiceGeneral from "Services/ServiceGeneral";
import UserSession from "Utils/UserSession";
import translator from "MultiLanguage/TranslationWrapper";
import AuthenticationError from "Error/AuthenticationError";
import qs from "qs";

/**
 * Manage user operations with the backend
 */
class UserService {
  /**
   * Login a user with the given credentials
   * @param {string} username The username
   * @param {string} password The password
   * @returns Promise with info of tokens if login successful
   * @throws Throws if an error happened when reaching back
   */
  static async login(username: string, password: string) {
    const url = Config.BACKEND.BASEURL + Config.BACKEND.PATHS.LOGIN;
    const params = qs.stringify({
      grant_type: "password",
      username: username,
      password: password,
    });

    try {
      // Set the tokens in cookies for future auth through middleware
      const response = await Axios.post(url, params);
      if (response.status !== 200) return response;

      response.data.expire_at = Date.now() + response.data.expires_in * 1000;
      UserSession.setTokens({
        access_token: response.data.token,
        refresh_token: response.data.refreshToken,
      });
      UserSession.setRole(response.data.role);
      UserSession.setId(response.data.user_id);

      return response;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);

      return data;
    }
  }

  /**
   * Get the informations about me
   * @returns The user informations
   */
  static async getUser(me: number) {
    const tokens = UserSession.getAccessToken();
    const data = {
      headers: {},
    };

    if (tokens !== undefined) {
      data.headers = {
        Authorization: tokens.token_type + " " + tokens.access_token,
      };
    }

    try {
      const response = await Axios.get(
        Config.BACKEND.BASEURL + Config.BACKEND.PATHS.GET_USER + "/" + me,
        data
      );
      if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
        return undefined;
      }
      return response;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);

      return data;
    }
  }

  static async getMe() {
    const user = UserSession.getUser();
    if (user === undefined) return this.getUser(user.id);
    return user;
  }

  static async getUsers() {
    const tokens = UserSession.getAccessToken();
    const data = {
      headers: {},
    };

    if (tokens !== undefined) {
      data.headers = {
        Authorization: tokens.token_type + " " + tokens.access_token,
      };
    }

    try {
      const response = await Axios.get(
        Config.BACKEND.BASEURL + Config.BACKEND.PATHS.GET_USER,
        data
      );
      if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
        return undefined;
      }
      return response.data;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);

      return data;
    }
  }

  static async addUser(user: any) {
    const url = Config.BACKEND.BASEURL + Config.BACKEND.PATHS.ADD_USER;
    const tokens = UserSession.getAccessToken();
    const data = {
      headers: {},
    };

    if (tokens !== undefined) {
      data.headers = {
        Authorization: tokens.token_type + " " + tokens.access_token,
      };
    }
    try {
      const response = await Axios.post(url, user, data);
      if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
        return undefined;
      }
      return response;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);
      return data;
    }
  }

  static async deleteUser(userId: number) {
    const url =
      Config.BACKEND.BASEURL + Config.BACKEND.PATHS.GET_USER + "/" + userId;
    const tokens = UserSession.getAccessToken();
    const data = {
      headers: {},
    };

    if (tokens !== undefined) {
      data.headers = {
        Authorization: tokens.token_type + " " + tokens.access_token,
      };
    }
    try {
      const response = await Axios.delete(url, data);
      if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
        return undefined;
      }
      return response;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);
      return data;
    }
  }

  static async setNewPassword(password: string, encrypted: string) {
    const url =
      Config.BACKEND.BASEURL +
      Config.BACKEND.PATHS.GET_USER +
      "/new_password/" +
      encrypted;
    const tokens = UserSession.getAccessToken();
    const data = {
      headers: {},
    };

    if (tokens !== undefined) {
      data.headers = {
        Authorization: tokens.token_type + " " + tokens.access_token,
      };
    }
    try {
      const response = await Axios.post(url, { password: password }, data);
      if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
        return undefined;
      }
      return response;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);
      return data;
    }
  }

  static async forgottenPassword(userMail: string) {
    const url =
      Config.BACKEND.BASEURL +
      Config.BACKEND.PATHS.GET_USER +
      "/password/" +
      userMail;
    const tokens = UserSession.getAccessToken();
    const data = {
      headers: {},
    };

    if (tokens !== undefined) {
      data.headers = {
        Authorization: tokens.token_type + " " + tokens.access_token,
      };
    }
    try {
      const response = await Axios.get(url, data);
      if ((await ServiceGeneral.handleAxiosResponse(response)) === "end") {
        return undefined;
      }
      return response;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);
      return data;
    }
  }

  static getUserRole() {
    return UserSession.getRole();
  }

  static refreshTokens() {
    return UserSession.timeOutSession();
  }

  /**
   * Signs out the current logged user
   */
  static signout() {
    UserSession.signOut();
  }
}

export default UserService;
